<script setup>
  import { onMounted, computed, ref, toRaw } from 'vue';
  import { useConfirmValidateStore } from '../../stores/ConfirmValidateStore';
  import Loader from '../../components/ui/Loader.vue';
  import { useI18n } from 'vue-i18n';
  import { useBrowseCartStore } from '../../stores/BrowseCartStore';
  import { marked } from 'marked';

  const validateStore = useConfirmValidateStore();
  const cartStore = useBrowseCartStore();
  const { t } = useI18n();
  const emits = defineEmits(['closeModal']);
  const props = defineProps({
    cartId: {
      type: Number,
      required: true
    }
  });

  const loading = computed(() => {
    return validateStore.loading;
  });

  const data = computed(() => {
    if(validateStore.data) { return validateStore.data; }
  });

  const initialAttributes = toRaw({
    id: '',
    name: '',
    address_full: ''
  });
  const selectedItem = ref({
    retailer: initialAttributes,
    sub_library: initialAttributes,
    buyer_type: initialAttributes
  });

  const formData = ref({
    name: '',
    po_number: null,
    retailer_id: selectedItem.value.retailer.id,
    sub_library_id: selectedItem.value.sub_library.id,
    buyer_type_id: selectedItem.value.buyer_type.id,
    customer_service: 0,
    confirmValidation: 0,
    show_retailers: false,
    validation_schedule: 'immediate',
    will_validate_at: null
  });

  const sendValidate = async () => {
    const params = { cart: formData.value }
    await validateStore.saveValidate(props.cartId, params);
    if(!validateStore.errors) {
      cartStore.cart.status = validateStore.success.status;
      cartStore.cart.will_validate_at = validateStore.success.will_validate_at;
      cartStore.cart.customer_service = validateStore.success.customer_service;
      cartStore.cart.policy = validateStore.success.policy;
      emits('closeModal');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const computedRetailers = computed(() => {
    if(validateStore.data.cart.retailer.retailers) {
      return validateStore.data.cart.retailer.retailers.map((retailer) => {
        return { key: retailer.id, label: retailer.name };
      });
    }
  });

  const setSelected = (item, type) => {
    if(type === 'retailer') {
      if (item === '') {
        selectedItem.value.retailer = initialAttributes;
        formData.value.retailer_id = '';
        return;
      }
      const retailer = validateStore.data.cart.retailer.retailers.find(retailer => retailer.id == item);
      selectedItem.value.retailer = retailer;
    } else if(type === 'sub_library') {
      if (item === '') {
        selectedItem.value.sub_library = initialAttributes;
        formData.value.sub_library_id = '';
        return;
      }
      const sub_library = validateStore.data.cart.sub_library.sub_libraries.find(sub_library => sub_library.id == item);
      selectedItem.value.sub_library = sub_library;
    } else if(type === 'buyer_type') {
      if (item === '') {
        selectedItem.value.buyer_type = initialAttributes;
        formData.value.buyer_type_id = '';
        return;
      }
      const buyer_type = validateStore.data.cart.buyer_type.buyer_types.find(buyer_type => buyer_type.id == item);
      selectedItem.value.buyer_type = buyer_type;
    }
  };

  onMounted (async() => {
    await validateStore.fetchDataValidate(props.cartId);
    formData.value.po_number = validateStore.data.cart.po_number.value;

    if(validateStore.data.cart.retailer.retailers && validateStore.data.cart.retailer.id) {
      const retailer = validateStore.data.cart.retailer.retailers.find((retailer) => {
        return retailer.id === validateStore.data.cart.retailer.id;
      });
      selectedItem.value.retailer = { id: retailer.id, name: retailer.name, address_full: retailer.address_full };
      formData.value.retailer_id = retailer.id;
    }

    if(validateStore.data.cart.sub_library.sub_libraries && validateStore.data.cart.sub_library.id) {
      const sub_library = validateStore.data.cart.sub_library.sub_libraries.find((sub_library) => {
        return sub_library.id === validateStore.data.cart.sub_library.id;
      });
      selectedItem.value.sub_library = { id: sub_library.id, name: sub_library.name, address_full: sub_library.address_full };
      formData.value.sub_library_id = sub_library.id;
    }

    if(validateStore.data.cart.buyer_type.buyer_types && validateStore.data.cart.buyer_type.id) {
      const buyer_type = validateStore.data.cart.buyer_type.buyer_types.find((buyer_type) => {
        return buyer_type.id === validateStore.data.cart.buyer_type.id;
      });
      selectedItem.value.buyer_type = { id: buyer_type.id, name: buyer_type.name, address_full: buyer_type.address_full };
      formData.value.buyer_type_id = buyer_type.id;
    }

    formData.value.show_retailers = validateStore.data.show_retailers;
    formData.value.validation_schedule = validateStore.data.cart.validation_schedule;
    formData.value.will_validate_at = validateStore.data.cart.will_validate_at;
  });

  const errors = computed(() => {
    if(validateStore.errors) {
      return validateStore.errors;
    }
  });

  // Retailer defined at the network level, overriding the selected one for customer service carts
  const forcedRetailer = computed(() => {
    return formData.value.customer_service && validateStore.data.customer_service_retailer;
  });

  const validateConfirmation = computed(() => {
    return (formData.value.retailer_id === '' && validateStore.data.cart.retailer.required && !forcedRetailer.value) || formData.value.confirmValidation == 0 || (validateStore.data.cart.buyer_type.buyer_types && formData.value.buyer_type_id === '') || (formData.value.validation_schedule === 'delayed' && !formData.value.will_validate_at);
  });

  const tomorrow = computed(() => {
    const tomorrowDate = new Date();
    tomorrowDate.setDate(tomorrowDate.getDate() + 1);
    return tomorrowDate.toISOString().split('T')[0];
  });

</script>

<template>
  <div class="alert alert-danger" role="status" v-if="errors">
    <h2 class="fs-5">
      {{ errors.title }}
    </h2>
    {{ errors.details }}
  </div>
  <Loader v-if="loading" />
  <div v-if="data && !loading">
    <div class="mb-3">
      <label for="po_number" class="form-label">{{ data.cart.po_number.label }}</label>
      <input type="text" class="form-control" id="po_number" name="po_number" v-model="formData.po_number" />
    </div>

    <div class="mb-3" v-if="data.cart.retailer.retailers && formData.show_retailers">
      <label for="selected_retailer_id" class="form-label">{{ t('cart.retailer.retailers') }}</label>
      <template v-if="forcedRetailer">
        <input type="text" class="form-control" id="selected_retailer_id" :value="forcedRetailer" disabled />
      </template>
      <template v-else-if="data.cart.already_processed">
        <input type="text" class="form-control" :id="selected_retailer_id" :name="selected_retailer_id" :value="data.cart.retailer?.name" disabled />
      </template>
      <template v-else>
        <select class="form-select" id="selected_retailer_id" name="selected_retailer_id" v-model="formData.retailer_id" @change="(e) => setSelected(e.target.value, 'retailer')">
          <option :key="0" value=""></option>
          <option v-for="retailer in computedRetailers" :key="retailer.key" :value="retailer.key">{{ retailer.label }}</option>
        </select>
      </template>
      <div class="alert alert-light mt-2 p-2" v-if="selectedItem.retailer.address_full && !forcedRetailer" v-html="selectedItem.retailer.address_full"></div>
    </div>

    <div class="mb-3" v-if="data.cart.sub_library.sub_libraries">
      <label for="selected_sub_library_id" class="form-label">{{ t('cart.sub_library.sub_libraries') }}</label>
      <template v-if="data.cart.already_processed">
        <input type="text" class="form-control" :id="selected_sub_library_id" :name="selected_sub_library_id" :value="data.cart.sub_library?.name" disabled />
      </template>
      <template v-else>
        <select class="form-select" id="selected_sub_library_id" name="selected_sub_library_id" v-model="formData.sub_library_id" @change="(e) => setSelected(e.target.value, 'sub_library')">
          <option :key="0" value=""></option>
          <option v-for="sub_library in data.cart.sub_library.sub_libraries" :key="sub_library.id" :value="sub_library.id">{{ sub_library.name }}</option>
        </select>
      </template>
      <div class="alert alert-light mt-2 p-2" v-if="selectedItem.sub_library.address_full" v-html="selectedItem.sub_library.address_full"></div>
    </div>

    <div class="mb-3" v-if="data.cart.buyer_type.buyer_types">
      <label for="selected_buyer_type_id" class="form-label">{{ t('cart.buyer_type.buyer_types') }}</label>
      <template v-if="data.cart.already_processed">
        <input type="text" class="form-control" :id="selected_buyer_type_id" :name="selected_buyer_type_id" :value="data.cart.buyer_type?.name" disabled />
      </template>
      <template v-else>
        <select class="form-select" id="selected_buyer_type_id" name="selected_buyer_type_id" v-model="formData.buyer_type_id" @change="(e) => setSelected(e.target.value, 'buyer_type')">
          <option :key="0" value=""></option>
          <option v-for="buyer_type in data.cart.buyer_type.buyer_types" :key="buyer_type.id" :value="buyer_type.id">{{ buyer_type.name }}</option>
        </select>
      </template>
    </div>

    <div class="mb-3">
      <div class="form-check">
        <input class="form-check-input" type="radio" name="validation_schedule" id="validation_schedule_immediate" v-model="formData.validation_schedule" value="immediate">
        <label class="form-check-label" for="validation_schedule_immediate">{{ t('cart.confirm_validation.immediate_validation') }}</label>
      </div>
    </div>
    <div class="mb-3">
      <div class="row align-items-center">
        <div class="col-auto">
          <div class="form-check">
            <input class="form-check-input" type="radio" name="validation_schedule" id="validation_schedule_delayed" v-model="formData.validation_schedule" value="delayed">
            <label class="form-check-label" for="validation_schedule_delayed">{{ t('cart.confirm_validation.delayed_validation') }}</label>
          </div>
        </div>
        <div class="col-auto">
          <input type="date" :min="tomorrow" v-model="formData.will_validate_at" :disabled="formData.validation_schedule != 'delayed'" class="form-control"/>
        </div>
      </div>
    </div>

    <div class="mb-3" v-if="data.admin_or_support">
      <template v-if="data.biblius_market">
        <div class="alert alert-primary" role="alert">
          {{ data.cart.labels.forced_customer_service }}
        </div>
      </template>
      <template v-else>
        <div class="form-check">
          <input class="form-check-input" type="checkbox" value="1" id="customer_service" name="customer_service" v-model="formData.customer_service">
          <label class="form-check-label" for="customer_service">{{ data.cart.labels.customer_service }}</label>
        </div>
      </template>
    </div>

    <div class="alert alert-warning" role="alert">
      <div class="mb-0" v-html="marked(t('cart.confirm_validation.text'))"></div>
      {{ t('cart.confirm_validation.particularly') }}:
      <ul class="mb-0">
        <li>
          {{ t('cart.confirm_validation.list.prices') }}
        </li>
        <li>
          {{ t('cart.confirm_validation.list.quantity') }}
        </li>
        <li>
          {{ t('cart.confirm_validation.list.formats') }}
        </li>
        <li>
          {{ t('cart.confirm_validation.list.licenses') }}
        </li>
      </ul>
    </div>

    <div class="form-check">
      <input class="form-check-input" type="checkbox" value="" id="confirmation_checkbox" v-model="formData.confirmValidation">
      <label class="form-check-label" for="confirmation_checkbox"><strong>{{ t('cart.confirm_validation.checkbox_label') }}</strong></label>
    </div>

    <div class="row">
      <div class="col text-end">
        <button type="button" class="btn btn-primary" @click.prevent="sendValidate" :disabled="validateConfirmation">{{ data.cart.labels.submit }}</button>
      </div>
    </div>
  </div>
</template>

<style scoped>
  .address {
    margin: 20px;
    display: block;
  }
  button[disabled] {
    opacity: 0.5;
    cursor: not-allowed;
  }
  .text-description >>> p {
    margin-block: 0 !important;
  }
  .text-description {
    margin: 10px 10px 0 10px;
  }
  .checkbox-element .form-field-container {
    margin-bottom: 0;
  }
  .form-row--compacted {
    flex-wrap: nowrap;
    justify-content: flex-start;
    align-items: center;
  }
  .form-row--compacted .form-field-container {
    min-width: 0;
    margin-right: 10px;
  }
</style>
